import { Fragment } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { Box, useTheme } from 'app/shopify/theme';
import { TabBarItem, type NavigationItem } from '../TabBarItem';
import { PublishButton, type PublishAction } from '../Navigation/PublishButton';

export interface BottomTabBarProps {
  items: readonly NavigationItem[];
  selectedKey?: string;
  onSelect: (key: string) => void;
  publish?: PublishAction;
  disabled?: boolean;
  /** Supply the device's bottom inset; the bar does not own a safe-area provider. */
  bottomInset?: number;
  /** Figma's Full composition, for previews only. Native devices draw their own indicator. */
  showHomeIndicator?: boolean;
  style?: StyleProp<ViewStyle>;
}

export function BottomTabBar({
  items,
  selectedKey,
  onSelect,
  publish,
  disabled = false,
  bottomInset = 0,
  showHomeIndicator = false,
  style,
}: BottomTabBarProps) {
  const theme = useTheme();
  const publishButton = publish && (
    <Box flex={1} alignItems="center" justifyContent="center">
      <PublishButton {...publish} placement="bottom" disabled={disabled || publish.disabled} />
    </Box>
  );
  return (
    <Box backgroundColor="surface.default" width="100%" style={style}>
      <Box flexDirection="row" alignItems="stretch" paddingTop="spacing.general.x1" paddingBottom="spacing.general.x3">
        {items.map(({ key, ...item }, index) => (
          <Fragment key={key}>
            {index === Math.floor(items.length / 2) && publishButton}
            <TabBarItem
              {...item}
              active={selectedKey === key}
              disabled={disabled || item.disabled}
              onPress={() => onSelect(key)}
              style={{ flex: 1, minWidth: 0 }}
            />
          </Fragment>
        ))}
        {items.length === 0 && publishButton}
      </Box>
      {showHomeIndicator && (
        // This system indicator's geometry is untokenized in Figma.
        <Box height={21} alignItems="center" justifyContent="center">
          <Box
            width={134}
            height={5}
            borderRadius="radius.full"
            style={{ backgroundColor: theme.colors['icon.primary'] }}
          />
        </Box>
      )}
      {bottomInset > 0 && <Box height={bottomInset} />}
    </Box>
  );
}
